import React from 'react';
import { Link } from 'react-router-dom';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 text-sm font-medium"
          >
            ← Back to Home
          </Link>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg px-8 py-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            VeriVox AI Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Last updated: October 2025
          </p>

          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
            This Privacy Policy describes how VeriVox AI Inc., based in Toronto,
            Canada (together with its affiliated companies, "VeriVox AI," "we,"
            "our" or "us"), collects, stores, uses and discloses personal data
            related to users, customers, freelancers, and visitors interacting
            with our AI-powered transcription, captioning, and language
            processing platform (the "Platform") and our related websites and
            services (collectively, the "Services").
          </p>

          <div className="prose prose-indigo dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                1. Data Collection & Processing
              </h2>
              <ul className="list-disc list-inside space-y-3 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  <strong>Customer Data</strong> – Data collected, processed,
                  and managed on behalf of our customers as part of the VeriVox
                  AI Platform. This includes identifiable information of
                  individuals who use our transcription, captioning, or
                  translation tools, upload or stream audio/video content, or
                  are otherwise identifiable in such content ("data subjects").
                </li>
                <li>
                  <strong>User Data</strong> – Contact, account, and billing
                  details of customer representatives, administrators, and
                  authorized users interacting with our Platform or company.
                </li>
                <li>
                  <strong>Website, CRM & Prospect Data</strong> – Information
                  about website visitors, partners, freelancers, event
                  participants, and other prospects who interact with VeriVox AI
                  online or through marketing communications.
                </li>
                <li>
                  <strong>Voice Data & Biometrics</strong> – Voice data
                  processed by VeriVox AI is handled securely, used solely for
                  service delivery (such as speaker differentiation), and
                  automatically deleted according to our retention schedule.
                  VeriVox AI does not sell, share, or maintain biometric
                  databases.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                2. Data Uses
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>Delivering and improving our Services.</li>
                <li>Authenticating users and freelancers.</li>
                <li>Providing customer and technical support.</li>
                <li>Optimizing user experience and marketing campaigns.</li>
                <li>Ensuring platform security and fraud prevention.</li>
                <li>
                  Complying with applicable privacy laws, including Canada's
                  PIPEDA and other international regulations as applicable.
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                We do not sell or share your personal data as defined under the
                California Consumer Privacy Act (CCPA) or Canadian privacy law
                equivalents.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                3. Data Location
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We store and process data in Canada, the United States, and
                  other jurisdictions as needed for service delivery.
                </li>
                <li>
                  All data transfers comply with PIPEDA and relevant
                  international privacy frameworks, including Standard
                  Contractual Clauses for cross-border transfers when required.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                4. Data Retention
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  Personal data is retained as long as necessary to provide our
                  Services, meet legal obligations, resolve disputes, and
                  enforce agreements.
                </li>
                <li>
                  Data may be anonymized or deleted once it is no longer needed.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                5. Data Sharing
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  <strong>Service Providers</strong> – Trusted partners
                  providing hosting, billing, analytics, and security services.
                </li>
                <li>
                  <strong>Business Partners</strong> – Distributors, resellers,
                  or affiliates assisting in delivering localized services.
                </li>
                <li>
                  <strong>Legal Authorities</strong> – When required by law or
                  to protect our rights or safety.
                </li>
                <li>
                  <strong>Customers</strong> – Where Customer Data is shared
                  within an organization's account for collaboration.
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                All recipients are bound by confidentiality and data protection
                obligations consistent with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                6. Cookies and Tracking Technologies
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We use cookies to operate, secure, and improve our website
                  experience.
                </li>
                <li>
                  You may manage cookie preferences via your browser settings.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                7. Communications
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  Service Communications (e.g., account updates, billing
                  notices).
                </li>
                <li>
                  Promotional Communications (e.g., new features or events).
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                You can unsubscribe from promotional messages anytime via the
                link provided in our emails or by contacting{' '}
                <a
                  href="mailto:support@verivox.ai"
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                >
                  support@verivox.ai
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                8. Data Security
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We employ advanced technical and organizational measures to
                safeguard personal data from unauthorized access, loss, misuse,
                or alteration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                9. Data Subject Rights
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                Under PIPEDA, GDPR, and similar laws, you may request access to,
                correction of, or deletion of your personal data, or object to
                its processing.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                To exercise your rights, contact us at{' '}
                <a
                  href="mailto:privacy@verivox.ai"
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                >
                  privacy@verivox.ai
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                10. Data Controller/Processor Roles
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  VeriVox AI is the data controller for Website, CRM, and
                  Prospect Data.
                </li>
                <li>
                  For Customer Data, VeriVox AI acts as a data processor,
                  processing on behalf of our customers in accordance with their
                  instructions.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                11. Additional Notices
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  <strong>Children</strong> – Our Services are not intended for
                  individuals under 16.
                </li>
                <li>
                  <strong>External Links</strong> – Third-party integrations
                  (e.g., Google Drive, Microsoft OneDrive, AWS) are governed by
                  their respective privacy policies.
                </li>
                <li>
                  <strong>Policy Updates</strong> – We may update this policy
                  from time to time. The latest version will always be available
                  on our website.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Contact Us
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                VeriVox AI Inc.
                <br />
                Toronto, Ontario, Canada
                <br />
                Email:{' '}
                <a
                  href="mailto:privacy@verivox.ai"
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                >
                  privacy@verivox.ai
                </a>
              </p>
            </section>

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                © VeriVox AI 2025 - All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
